import { Link } from 'react-router-dom';
import { Table } from 'reactstrap';
import TideForms from '../marinetools/components/tides/TideForms.jsx';
import tidesStandardPorts from '../data/tidesStandardPorts.js';

export default function TidesStandardPorts() {
  return (
    <section className="as-section">
      <div className="container">
        <h1 className="as-page-title">Tides &ndash; Standard Ports</h1>
        <p className="as-lead">
          For the purposes of this module and its exercises, we will be using the 2025
          Queensland Tide Tables published by the Queensland Government. You should
          download this pdf and use it as the tide reference with this module.
        </p>

        <h2 className="as-subheading">Standard Ports</h2>
        <p>
          Standard ports are those provided as daily tables of the predicted times and
          heights of high and low waters. The tide times are referred to Australian
          Eastern Standard Time (AEST +1000) and the tide heights are referred to LAT
          datum. The 24-hour clock is used in the Time Tables. For example 06:00 is
          equivalent of 6am and 13:00 is the same as 1pm.
        </p>
        <p>Following is an example extract of Tide Levels on 16th of April 2025 for Brisbane Bar:</p>

        <Table bordered responsive className="as-data-table">
          <thead>
            <tr><th>Description</th><th>Time</th><th>Metres from (LAT)</th></tr>
          </thead>
          <tbody>
            <tr><td>Low Water/Tide (LW)</td><td>05:32</td><td>0.73m</td></tr>
            <tr><td>High Water/Tide (HW)</td><td>11:04</td><td>1.88m</td></tr>
            <tr><td>Low Water/Tide (LW)</td><td>17:12</td><td>0.56m</td></tr>
            <tr><td>High Water/Tide (HW)</td><td>23:41</td><td>2.33m</td></tr>
          </tbody>
        </Table>

        <h3 className="as-subheading-sm">Calculating Tide Range</h3>
        <p>
          The range of the tide is the difference between the height of high water and
          the next succeeding or last preceding low water. Using above example to find
          the tide height difference (Tide Range) for 16th of April 2025 for 09:30,
          subtract the low water from high water to get the range:
        </p>
        <Table bordered responsive className="as-data-table">
          <tbody>
            <tr><td>High Water</td><td>1.88m</td></tr>
            <tr><td>Low Water</td><td>0.73m</td></tr>
            <tr><td>Range (Height Difference)</td><td>1.15m</td></tr>
          </tbody>
        </Table>

        <h2 className="as-subheading">Standard Tidal Curves</h2>
        <p>
          Standard Tidal Curves are graphical tools used in maritime navigation to find
          the height of the tide at any time between high and low water, using data from
          tide tables for a &ldquo;Standard Port&rdquo; (like Brisbane Bar or Mackay).
          This is called an intermediate tidal height.
        </p>
        <p>
          They feature time on the horizontal axis and height on the vertical, with a
          bell-shaped curve showing how tide height changes over the tidal cycle (usually
          ~12.4 hours) for both spring and neap tides, allowing navigators to determine
          available depths for vessels.
        </p>
        <p>
          Following are Standard Tidal Curves from the 2025 Queensland Tide Tables used
          to calculate intermediate tide heights.
        </p>

        <h3 className="as-subheading-sm">How They Work</h3>
        <ul>
          <li>Get Standard Port Data: Obtain the predicted times and heights of High Water (HW) and Low Water (LW) for a Standard Port from a tide table.</li>
          <li>Determine Tidal Range: Calculate the difference between HW and LW height (the Range).</li>
          <li>Plot on the Curve: Use a plotter or straight edge to draw a line on the curve corresponding to your tidal range (e.g., 1.15m range).</li>
          <li>Find Time: Mark the time of High Water on the curve and count the hours (or fractions of hours) before or after it.</li>
          <li>Read the Height: Find your desired time on the curve, move across to the height scale to see the water level.</li>
        </ul>

        <h3 className="as-subheading-sm">Key Components</h3>
        <ul>
          <li>Time Axis: Shows hours before/after HW (e.g., -6 to +6 hours).</li>
          <li>Height Scale: Vertical scale showing water height (meters).</li>
          <li>Tidal Curve (Bell Curve): The curved line showing the rate of rise/fall; steeper at first, flatter near HW/LW.</li>
        </ul>

        <h3 className="as-subheading-sm">Example use of the Standard Tidal Curve</h3>
        <p>
          Continuing with above example, we need to find tidal height at 09:30, chosen as
          a time between the LW at 05:32 and HW at 11:04, taking following steps:
        </p>
        <ul>
          <li>Find time difference from High Water: 11:04 (HW) &ndash; 09:30 (Desired Time) = 1 hour and 34 minutes before high water.</li>
          <li>Tidal Range: 1.88m (HW) &ndash; 0.73m (LW) = 1.15m</li>
          <li>Using the 3m Standard Tidal Curves graph, find the 1 hour 34 minutes mark before high water on the horizontal axis (red line on the graph).</li>
          <li>Follow the line up to the projected 1.15m tidal curve (green line on the graph) and read the corresponding height on the vertical axis (blue line on the graph).</li>
        </ul>
        <p>In this scenario, the curve shows the height to be added to the low water is approximately 0.95m</p>

        <h3 className="as-subheading-sm">Calculate Actual Height</h3>
        <ul>
          <li>Height at 09:30 am = Low Water Height + Curve Height</li>
          <li>Result: 0.73m + 0.95m = 1.68m</li>
        </ul>
        <p>At 09:30, the tide height is approximately 1.68 meters above the Lowest Astronomical Tide (LAT)</p>

        <h2 className="as-subheading">Progress Check</h2>
        <p>
          Complete the exercises in each tab to check your progress for standard ports.
          Please use the Queensland MSQ 2025 Tide Tables as the exclusive reference in all
          the exercises. Any tide times should be formatted as HH:mm (Example: 09:35).
          This would typically show as 0935 in the Queensland Tide tables.
        </p>

        <TideForms data={tidesStandardPorts} />

        <div className="d-flex justify-content-between mt-4">
          <Link to="/tides" className="btn-as-outline">&larr; Previous &ndash; Introduction</Link>
          <Link to="/tides-secondary-ports" className="btn-as-primary">Next &ndash; Secondary Ports</Link>
        </div>
      </div>
    </section>
  );
}
